import { useMutation, useQueryClient } from '@tanstack/react-query'
import irisApi from '../lib/api'
import type { UploadedFile, UploadSession } from '../types'

interface UploadProgress {
  loaded: number
  total: number
  percent: number
}

interface UploadFileParams {
  slotId: string
  file: File
  documentOptionKey?: string
  onProgress?: (progress: UploadProgress) => void
}

function simulateDemoUpload(
  queryClient: ReturnType<typeof useQueryClient>,
  token: string,
  params: UploadFileParams,
): Promise<UploadedFile> {
  return new Promise((resolve) => {
    const previewUrl = URL.createObjectURL(params.file)
    const uploaded: UploadedFile = {
      uuid: crypto.randomUUID(),
      original_filename: params.file.name,
      mime_type: params.file.type,
      file_size: params.file.size,
      file_type: params.file.type.startsWith('image/') ? 'image' : 'document',
      document_option_key: params.documentOptionKey || '',
      url: previewUrl,
      thumbnail_url: params.file.type.startsWith('image/') ? previewUrl : null,
      created_at: new Date().toISOString(),
    }

    setTimeout(() => {
      queryClient.setQueryData<UploadSession>(
        ['upload-session', token],
        (old) => {
          if (!old) return old
          return {
            ...old,
            slots: old.slots.map((s) =>
              s.id === params.slotId
                ? { ...s, uploads: [...s.uploads, uploaded] }
                : s,
            ),
          }
        },
      )
      resolve(uploaded)
    }, 800)
  })
}

export function useUploadFile(token: string, isDemo = false) {
  const queryClient = useQueryClient()

  return useMutation<UploadedFile, Error, UploadFileParams>({
    mutationFn: (params) => {
      if (isDemo) return simulateDemoUpload(queryClient, token, params)

      const form = new FormData()
      form.append('slot_id', params.slotId)
      form.append('file', params.file)
      if (params.documentOptionKey) form.append('document_option_key', params.documentOptionKey)

      return irisApi
        .post(`/${token}/files/`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total && params.onProgress) {
              params.onProgress({
                loaded: e.loaded,
                total: e.total,
                percent: Math.round((e.loaded / e.total) * 100),
              })
            }
          },
        })
        .then((r) => r.data)
    },
    onSuccess: (uploaded, params) => {
      if (isDemo) return
      // The POST response omits a couple of session-serializer fields.
      const entry: UploadedFile = {
        ...uploaded,
        thumbnail_url: uploaded.thumbnail_url ?? null,
        created_at: uploaded.created_at ?? new Date().toISOString(),
      }
      // Write the response straight into the session cache so the photo
      // appears immediately; the 30s session poll keeps it in sync.
      queryClient.setQueryData<UploadSession>(['upload-session', token], (old) => {
        if (!old) return old
        return {
          ...old,
          slots: old.slots.map((s) =>
            s.id === params.slotId && !s.uploads.some((u) => u.uuid === entry.uuid)
              ? { ...s, uploads: [...s.uploads, entry] }
              : s,
          ),
        }
      })
    },
  })
}
